import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';

import { useMapInteractionStore, useMapStateStore } from '../../../stores';
import { getLatLngFromFocusXYZ, animateMapNavigation } from '../GoogleMap/GoogleMapHelpers';
import { clusterOverlappingLabels } from '../MapPlace/MapPlaceLabelClusterHelpers';
import {
  getZoomScale,
  pickPrimaryLevelOfCareUuid,
  pickZoomZoneTargetLevelSvgId,
} from './MapOverlayHelpers';
import MapLabels from '../MapLabels/MapLabels';
import { getCenterFromPlace } from '../MapLabels/MapLabelsHelpers';

const ZOOM_ZONE_LEVEL_SWITCH_DELAY = 600;

const MapOverlayLabels = ( {
  places,
  units,
  unitsVersion,
  isBirdsEye,
  floorZoomZones,
  defaultLevelOfCare,
  parsedViewBox,
  clusterViewBox,
  containerSize,
  activeZoom,
  maxZoom,
  effectiveMaxZoom,
  mapZoomToEnableLevels,
  zoomZonesActivationLevel,
  platformClusterPlaceLabels,
  platformShowOccupancy,
  platformShowMapPlaceLabelsInitially,
  platformShowLowerValuePlaceLabelsInitially,
  platformShowZoomZonesOnMapLevels,
  markersVisible,
  isAmenitiesGuideActive,
  isZoomZonesGuideActive,
  zoomZones,
  svgData,
  onMarkerClick,
} ) => {
  const map = useMapInteractionStore( ( state ) => state.map );
  const overlayBounds = useMapInteractionStore( ( state ) => state.overlayBounds );
  const setActiveLevelOfCareUuid = useMapInteractionStore(
    ( state ) => state.setActiveLevelOfCareUuid,
  );
  const svgFocusId = useMapStateStore( ( state ) => state.svgFocusId );
  const setActiveLevelSvgId = useMapStateStore( ( state ) => state.setActiveLevelSvgId );
  const initialLevelSvgId = useMapStateStore( ( state ) => state.initialLevelSvgId );
  const lastClusterRevealRef = useRef( null );
  const zoomZoneLevelSwitchTimeoutRef = useRef( null );

  useEffect( () => () => {
    if ( zoomZoneLevelSwitchTimeoutRef.current ) {
      clearTimeout( zoomZoneLevelSwitchTimeoutRef.current );
    }
  }, [] );

  const [clusters, setClusters] = useState( [] );
  const [unclusteredPlaces, setUnclusteredPlaces] = useState( [] );

  // Recalculate clusters when zoom / container / places change
  useEffect( () => {
    if ( !places || places.length === 0 ) {
      setClusters( [] );
      setUnclusteredPlaces( [] );
      return undefined;
    }

    // Wait for valid container dimensions before clustering
    if ( !containerSize.width || !containerSize.height ) {
      setClusters( [] );
      setUnclusteredPlaces( places );
      return undefined;
    }

    const timeoutId = setTimeout( () => {
      const linkPlaces = places.filter( ( p ) => p?.pin?.create_modal );
      const nonLinkPlaces = places.filter( ( p ) => !p?.pin?.create_modal );

      const actualMaxZoom = effectiveMaxZoom ?? maxZoom;
      const isAtMaxZoom = actualMaxZoom && activeZoom >= actualMaxZoom;

      if ( !platformClusterPlaceLabels || isAtMaxZoom ) {
        setClusters( [] );
        setUnclusteredPlaces( places );
        return;
      }

      const zoomScale = getZoomScale( activeZoom, mapZoomToEnableLevels );

      // Non-link ("lower value") labels participate in clustering only when
      // they're actually shown on the map. Otherwise they have no visible
      // footprint to collide with and should stay out of the cluster input.
      const placesToCluster = platformShowLowerValuePlaceLabelsInitially
        ? places
        : linkPlaces;
      const placesAlwaysUnclustered = platformShowLowerValuePlaceLabelsInitially
        ? []
        : nonLinkPlaces;

      const {
        clusters: newClusters,
        unclustered,
      } = clusterOverlappingLabels(
        placesToCluster,
        clusterViewBox,
        zoomScale,
        false,
        containerSize,
      );

      const allUnclustered = [...placesAlwaysUnclustered, ...unclustered];
      setClusters( newClusters );
      setUnclusteredPlaces( allUnclustered );
    }, 150 );

    return () => clearTimeout( timeoutId );
  }, [places, platformClusterPlaceLabels, platformShowLowerValuePlaceLabelsInitially,
    activeZoom, maxZoom, effectiveMaxZoom, clusterViewBox, mapZoomToEnableLevels,
    containerSize] );

  // Reset reveal tracking when the focused amenity changes so a new
  // selection can re-trigger the cluster reveal flow.
  useEffect( () => {
    lastClusterRevealRef.current = null;
  }, [svgFocusId] );

  // If the focused amenity is currently hidden inside a cluster badge,
  // zoom in toward the label's true center until clustering breaks apart
  // and the individual label is revealed. Each zoom step recomputes
  // clusters and re-fires this effect, so the reveal happens iteratively.
  useEffect( () => {
    if ( !svgFocusId || !map || !overlayBounds || !svgData ) return;
    if ( !clusters || clusters.length === 0 ) return;

    const actualMaxZoom = effectiveMaxZoom ?? maxZoom;
    const currentZoom = map.getZoom();
    if ( actualMaxZoom && currentZoom >= actualMaxZoom ) return;

    let labelCenter = null;
    for ( let i = 0; i < clusters.length; i += 1 ) {
      const cluster = clusters[i];
      const idx = cluster.labels.findIndex( ( p ) => p?.pin?.svg_id === svgFocusId );
      if ( idx !== -1 ) {
        labelCenter = cluster.svgCenters[idx] || cluster.center;
        break;
      }
    }
    if ( !labelCenter ) return;

    // Avoid re-issuing the same zoom for the same focus while the
    // animation is still in flight (clusters may re-render mid-anim).
    const revealKey = `${svgFocusId}:${currentZoom}`;
    if ( lastClusterRevealRef.current === revealKey ) return;
    lastClusterRevealRef.current = revealKey;

    const nextZoom = actualMaxZoom
      ? Math.min( currentZoom + 1, actualMaxZoom )
      : currentZoom + 1;
    const focusXYZ = { x: labelCenter.x, y: labelCenter.y, z: nextZoom };
    const targetLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
    animateMapNavigation( map, targetLatLng, nextZoom );
  }, [svgFocusId, clusters, map, overlayBounds, svgData, effectiveMaxZoom, maxZoom] );

  const handleClusterClick = useCallback( ( cluster ) => {
    if ( map && overlayBounds && svgData && cluster.center ) {
      const currentZoom = map.getZoom();
      const nextZoom = currentZoom + 1;
      const focusXYZ = { x: cluster.center.x, y: cluster.center.y, z: nextZoom };
      const clusterLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
      animateMapNavigation( map, clusterLatLng, nextZoom );
    }
  }, [map, overlayBounds, svgData] );

  // Per-zone footprint bbox in SVG coords, keyed by map_building_grouping uuid
  // — the same uuid a zoom zone carries, so a zone can look up its own
  // building. Built from the centres of every place and unit tagged with that
  // grouping. The collision pass uses it to bound step 3: when a zone label has
  // no clean orientation where it stands, the marker may be shifted, but only
  // to somewhere still on its own building.
  //
  // It is the bbox of centres, not of shape outlines — an approximation, and
  // deliberately a loose one. A Drupal-computed shape footprint on each
  // zoom_zone would be tighter if that is ever exposed.
  //
  // Computed here rather than in MapLabels because the zoom-zone MapLabels
  // instances are rendered with no places and no units: this component is the
  // only place the raw data exists.
  const zoneFootprints = useMemo( () => {
    const result = new Map();
    const accumulate = ( uuid, x, y ) => {
      if ( !uuid ) return;
      if ( x == null || Number.isNaN( x ) || y == null || Number.isNaN( y ) ) return;
      const existing = result.get( uuid );
      if ( existing ) {
        existing.minX = Math.min( existing.minX, x );
        existing.minY = Math.min( existing.minY, y );
        existing.maxX = Math.max( existing.maxX, x );
        existing.maxY = Math.max( existing.maxY, y );
      } else {
        result.set( uuid, {
          minX: x, minY: y, maxX: x, maxY: y,
        } );
      }
    };
    places?.forEach( ( p ) => {
      const center = getCenterFromPlace( p );
      if ( !center ) return;
      accumulate( p?.map_building_grouping?.uuid, center.x, center.y );
    } );
    units?.forEach( ( u ) => {
      const lbl = u?.label;
      const x = lbl?.x !== undefined ? parseFloat( lbl.x ) : null;
      const y = lbl?.y !== undefined ? parseFloat( lbl.y ) : null;
      accumulate( u?.info?.map_building_grouping?.uuid, x, y );
    } );
    return result;
  }, [places, units] );

  const handleZoomZoneClick = useCallback( ( zoomZone ) => {
    if ( map && overlayBounds && svgData && zoomZone.center ) {
      // From bird's-eye, zoom into the floor band (mapZoomToEnableLevels) and
      // defer the level switch so it happens after the zoom animation passes
      // the level-enable threshold. From a floor, the user is already past
      // that threshold — pan to the zone center at the current zoom and
      // switch the level immediately so the click feels responsive.
      const currentZoom = map.getZoom();
      const targetZoom = isBirdsEye
        ? ( mapZoomToEnableLevels || currentZoom + 1 )
        : currentZoom;
      const focusXYZ = { x: zoomZone.center.x, y: zoomZone.center.y, z: targetZoom };
      const zoneLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
      animateMapNavigation( map, zoneLatLng, targetZoom );

      const primaryLocUuid = pickPrimaryLevelOfCareUuid( zoomZone.level_of_care )
        || defaultLevelOfCare;
      if ( primaryLocUuid ) {
        setActiveLevelOfCareUuid( primaryLocUuid );
      }

      const targetLevelSvgId = pickZoomZoneTargetLevelSvgId( zoomZone, initialLevelSvgId );
      if ( targetLevelSvgId ) {
        if ( zoomZoneLevelSwitchTimeoutRef.current ) {
          clearTimeout( zoomZoneLevelSwitchTimeoutRef.current );
        }
        if ( isBirdsEye ) {
          zoomZoneLevelSwitchTimeoutRef.current = setTimeout( () => {
            setActiveLevelSvgId( targetLevelSvgId );
            zoomZoneLevelSwitchTimeoutRef.current = null;
          }, ZOOM_ZONE_LEVEL_SWITCH_DELAY );
        } else {
          setActiveLevelSvgId( targetLevelSvgId );
        }
      }
    }
  }, [map, overlayBounds, svgData, mapZoomToEnableLevels, isBirdsEye,
    defaultLevelOfCare, initialLevelSvgId,
    setActiveLevelSvgId, setActiveLevelOfCareUuid] );

  const hasFloorZoomZones = !isBirdsEye
    && platformShowZoomZonesOnMapLevels
    && floorZoomZones
    && Object.keys(floorZoomZones).length > 0;

  return (
    <>
      {!isBirdsEye && parsedViewBox && (
        <MapLabels
          places={unclusteredPlaces}
          clusters={clusters}
          units={units}
          unitsVersion={unitsVersion}
          unclusteredPlaces={unclusteredPlaces}
          clusteringEnabled={platformClusterPlaceLabels}
          zoomZones={{}}
          showZoomZones={false}
          showUnitLabels
          showPlaceLabels={markersVisible}
          platformShowOccupancy={platformShowOccupancy}
          platformShowMapPlaceLabelsInitially={platformShowMapPlaceLabelsInitially}
          platformShowLowerValuePlaceLabelsInitially={platformShowLowerValuePlaceLabelsInitially}
          isAmenitiesGuideActive={isAmenitiesGuideActive}
          viewBox={parsedViewBox}
          activeZoom={activeZoom}
          baseZoom={mapZoomToEnableLevels || 18}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
        />
      )}

      {isBirdsEye && markersVisible && activeZoom >= zoomZonesActivationLevel && parsedViewBox && (
        <MapLabels
          places={[]}
          clusters={[]}
          unclusteredPlaces={[]}
          clusteringEnabled={false}
          zoomZones={zoomZones}
          showZoomZones
          zoneFootprints={zoneFootprints}
          isZoomZonesGuideActive={isZoomZonesGuideActive}
          viewBox={parsedViewBox}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
        />
      )}

      {hasFloorZoomZones && markersVisible && parsedViewBox && (
        <MapLabels
          places={[]}
          clusters={[]}
          unclusteredPlaces={[]}
          clusteringEnabled={false}
          zoomZones={floorZoomZones}
          showZoomZones
          zoomZonesOnFloor
          zoneFootprints={zoneFootprints}
          isZoomZonesGuideActive={isZoomZonesGuideActive}
          viewBox={parsedViewBox}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
        />
      )}
    </>
  );
};

// Place / zone / SVG payloads come straight from the map API and are handed
// on to MapLabels, which owns their detailed shapes.
const apiObject = PropTypes.shape( {} );

MapOverlayLabels.propTypes = {
  places: PropTypes.arrayOf( apiObject ),
  units: PropTypes.arrayOf( apiObject ),
  unitsVersion: PropTypes.number,
  // Bird's-eye renders the community-wide zones; a map level renders
  // floorZoomZones instead, gated on platformShowZoomZonesOnMapLevels.
  isBirdsEye: PropTypes.bool,
  floorZoomZones: PropTypes.objectOf( apiObject ),
  zoomZones: PropTypes.objectOf( apiObject ),
  // Community-level fallback LoC uuid, used when a clicked zone resolves none.
  defaultLevelOfCare: PropTypes.string,
  parsedViewBox: PropTypes.shape( {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  } ),
  clusterViewBox: PropTypes.shape( {
    width: PropTypes.number,
    height: PropTypes.number,
  } ),
  containerSize: PropTypes.shape( {
    width: PropTypes.number,
    height: PropTypes.number,
  } ),
  activeZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  effectiveMaxZoom: PropTypes.number,
  mapZoomToEnableLevels: PropTypes.number,
  zoomZonesActivationLevel: PropTypes.number,
  platformClusterPlaceLabels: PropTypes.bool,
  platformShowOccupancy: PropTypes.bool,
  platformShowMapPlaceLabelsInitially: PropTypes.bool,
  platformShowLowerValuePlaceLabelsInitially: PropTypes.bool,
  platformShowZoomZonesOnMapLevels: PropTypes.bool,
  markersVisible: PropTypes.bool,
  isAmenitiesGuideActive: PropTypes.bool,
  isZoomZonesGuideActive: PropTypes.bool,
  // Parsed SVG geometry, needed to convert a focus point to a LatLng.
  svgData: apiObject,
  onMarkerClick: PropTypes.func,
};

MapOverlayLabels.defaultProps = {
  places: [],
  units: [],
  unitsVersion: 0,
  isBirdsEye: false,
  floorZoomZones: {},
  zoomZones: {},
  defaultLevelOfCare: undefined,
  parsedViewBox: undefined,
  clusterViewBox: undefined,
  containerSize: undefined,
  activeZoom: undefined,
  maxZoom: undefined,
  effectiveMaxZoom: undefined,
  mapZoomToEnableLevels: undefined,
  zoomZonesActivationLevel: undefined,
  platformClusterPlaceLabels: false,
  platformShowOccupancy: false,
  platformShowMapPlaceLabelsInitially: false,
  platformShowLowerValuePlaceLabelsInitially: false,
  platformShowZoomZonesOnMapLevels: false,
  markersVisible: false,
  isAmenitiesGuideActive: false,
  isZoomZonesGuideActive: false,
  svgData: undefined,
  onMarkerClick: undefined,
};

export default MapOverlayLabels;
